import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackLanguageChange } from '../../utils/analytics';
import debug from '../../utils/debug';

const LanguageSelector: React.FC = () => {
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (langCode: string) => {
    if (langCode !== currentLanguage) {
      console.log(`[LanguageSelector] Language selection changed from ${currentLanguage} to ${langCode}`);
      debug.log(`Language selection changed from ${currentLanguage} to ${langCode}`);

      // Log current i18n state
      console.log(`[LanguageSelector] Current i18n language before change:`, (window as any).i18n?.language);
      console.log(`[LanguageSelector] Available i18n resources:`, Object.keys((window as any).i18n?.store?.data || {}));

      // Track the language change for analytics
      trackLanguageChange(currentLanguage, langCode);

      // Change the language
      changeLanguage(langCode);

      // Log after change request
      setTimeout(() => {
        console.log(`[LanguageSelector] i18n language after change request:`, (window as any).i18n?.language);
        console.log(`[LanguageSelector] Chinese resource bundle exists:`, !!(window as any).i18n?.hasResourceBundle?.('zh', 'translation'));
      }, 100);
    }
    setIsOpen(false);
  };

  const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-blue-800 hover:text-white focus:outline-none transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-lg">{currentLang?.flag}</span>
        <span className="hidden sm:inline">{currentLang?.name}</span>
        <svg
          className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-0' : 'transform rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-10">
          <ul
            className="py-1"
            role="listbox"
            aria-labelledby="language-selector"
            aria-activedescendant={currentLanguage}
            tabIndex={0}
          >
            {supportedLanguages.map(lang => (
              <li
                key={lang.code}
                role="option"
                aria-selected={lang.code === currentLanguage}
                id={lang.code}
                className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-blue-800 hover:text-white transition-colors text-black ${
                  lang.code === currentLanguage ? 'bg-blue-100 text-blue-800' : ''
                }`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="mr-2">{lang.flag}</span>
                <span>{lang.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;